import { useState, useEffect, useRef, useCallback } from 'react';
import useNarration from '../hooks/useNarration';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Scale as A1, GitBranch as A2, Wallet as A3, Clock as A4, Target as A5, AlertTriangle as A6 } from 'lucide-react';

import { useActiveChild } from '../context/ActiveChildContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import ParentViewBanner from '../components/ParentViewBanner';
import { Button } from '../components/ui/button';
import {
  GraduationCap, Briefcase, DollarSign, TrendingUp, Target, Award,
  Trophy, ChevronRight, Sparkles, MapPin, Star, Rocket, Shield, Wrench,
  ArrowRight, Clock, CheckCircle, AlertCircle, User, BookOpen,
  BarChart3, Zap, RefreshCw, Crown, ChevronDown, ChevronUp,
  Activity, Layers, Lightbulb, Navigation, Send, MessageCircle,
  ToggleLeft, Compass, Scale, ShieldCheck, Timer, Gauge,
  X, ArrowLeftRight, Lock, Unlock, Globe, Home as HomeIcon,
  Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATH_ICONS = {
  "graduation-cap": GraduationCap, "arrow-right-circle": ArrowRight, "wrench": Wrench,
  "shield": Shield, "rocket": Rocket, "briefcase": Briefcase, "refresh-cw": RefreshCw,
};

const COLOR_MAP = {
  blue: { bg: "from-blue-600/20 to-blue-500/5", border: "border-blue-500/30", text: "text-blue-400", accent: "bg-blue-500", light: "bg-blue-500/15", hex: "#3b82f6", ring: "ring-blue-500/30" },
  purple: { bg: "from-purple-600/20 to-purple-500/5", border: "border-purple-500/30", text: "text-purple-400", accent: "bg-purple-500", light: "bg-purple-500/15", hex: "#a855f7", ring: "ring-purple-500/30" },
  orange: { bg: "from-orange-600/20 to-orange-500/5", border: "border-orange-500/30", text: "text-orange-400", accent: "bg-orange-500", light: "bg-orange-500/15", hex: "#f97316", ring: "ring-orange-500/30" },
  emerald: { bg: "from-emerald-600/20 to-emerald-500/5", border: "border-emerald-500/30", text: "text-emerald-400", accent: "bg-emerald-500", light: "bg-emerald-500/15", hex: "#10b981", ring: "ring-emerald-500/30" },
  amber: { bg: "from-amber-600/20 to-amber-500/5", border: "border-amber-500/30", text: "text-amber-400", accent: "bg-amber-500", light: "bg-amber-500/15", hex: "#f59e0b", ring: "ring-amber-500/30" },
  cyan: { bg: "from-cyan-600/20 to-cyan-500/5", border: "border-cyan-500/30", text: "text-cyan-400", accent: "bg-cyan-500", light: "bg-cyan-500/15", hex: "#06b6d4", ring: "ring-cyan-500/30" },
  violet: { bg: "from-violet-600/20 to-violet-500/5", border: "border-violet-500/30", text: "text-violet-400", accent: "bg-violet-500", light: "bg-violet-500/15", hex: "#8b5cf6", ring: "ring-violet-500/30" },
};

const SCORE_META = {
  fit: { label: "Fit", icon: Target, color: "text-blue-400", desc: "How well this matches your goals" },
  affordability: { label: "Afford", icon: DollarSign, color: "text-emerald-400", desc: "Financial accessibility" },
  speed: { label: "Speed", icon: Timer, color: "text-amber-400", desc: "Time to first income" },
  outcome: { label: "Outcome", icon: TrendingUp, color: "text-purple-400", desc: "5-year earning potential" },
  risk: { label: "Risk", icon: ShieldCheck, color: "text-cyan-400", desc: "Completion & stability" },
  flexibility: { label: "Flex", icon: Compass, color: "text-orange-400", desc: "Ability to pivot later" },
};

const DESIGNATION_STYLES = {
  primary: { bg: "bg-gradient-to-r from-amber-500/20 to-amber-400/5", border: "border-amber-500/40", text: "text-amber-400", icon: Crown, badge: "bg-amber-500/20 text-amber-300" },
  backup: { bg: "bg-gradient-to-r from-blue-500/15 to-blue-400/5", border: "border-blue-500/30", text: "text-blue-400", icon: Shield, badge: "bg-blue-500/20 text-blue-300" },
  safety: { bg: "bg-gradient-to-r from-emerald-500/15 to-emerald-400/5", border: "border-emerald-500/30", text: "text-emerald-400", icon: ShieldCheck, badge: "bg-emerald-500/20 text-emerald-300" },
  other: { bg: "bg-slate-800/30", border: "border-slate-700/40", text: "text-slate-300", icon: Layers, badge: "bg-slate-700/40 text-slate-300" },
};

const SCENARIO_ICONS = {
  default: Scale, cheapest: DollarSign, fastest: Timer, best_roi: TrendingUp,
  lowest_debt: Lock, stay_local: HomeIcon, military_first: Shield,
  cc_first: ArrowRight, no_sat: Unlock, in_state: Globe,
};

const MILESTONE_CAT = {
  planning: { color: "text-blue-400", bg: "bg-blue-500/15", dot: "bg-blue-500" },
  exam: { color: "text-amber-400", bg: "bg-amber-500/15", dot: "bg-amber-500" },
  financial: { color: "text-emerald-400", bg: "bg-emerald-500/15", dot: "bg-emerald-500" },
  application: { color: "text-purple-400", bg: "bg-purple-500/15", dot: "bg-purple-500" },
  enrollment: { color: "text-cyan-400", bg: "bg-cyan-500/15", dot: "bg-cyan-500" },
  education: { color: "text-indigo-400", bg: "bg-indigo-500/15", dot: "bg-indigo-500" },
  career: { color: "text-orange-400", bg: "bg-orange-500/15", dot: "bg-orange-500" },
  transition: { color: "text-rose-400", bg: "bg-rose-500/15", dot: "bg-rose-500" },
};

/* ─── Radar Chart (Pure CSS/SVG) ────────────────────────────── */
function RadarChart({ scores, color = "#3b82f6", size = 200 }) {
  const keys = Object.keys(SCORE_META);
  const cx = size / 2, cy = size / 2, r = size * 0.38;
  const angleStep = (2 * Math.PI) / keys.length;

  const gridLevels = [0.25, 0.5, 0.75, 1.0];
  const pointsFor = (vals) => keys.map((k, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const v = (vals[k] || 0) / 100;
    return `${cx + r * v * Math.cos(angle)},${cy + r * v * Math.sin(angle)}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" data-testid="radar-chart">
      {/* Grid */}
      {gridLevels.map(lv => (
        <polygon key={lv} points={keys.map((_, i) => {
          const angle = i * angleStep - Math.PI / 2;
          return `${cx + r * lv * Math.cos(angle)},${cy + r * lv * Math.sin(angle)}`;
        }).join(' ')} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="0.5" />
      ))}
      {/* Axes */}
      {keys.map((k, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const lx = cx + r * Math.cos(angle);
        const ly = cy + r * Math.sin(angle);
        const tx = cx + (r + 16) * Math.cos(angle);
        const ty = cy + (r + 16) * Math.sin(angle);
        return (
          <g key={k}>
            <line x1={cx} y1={cy} x2={lx} y2={ly} stroke="rgba(148,163,184,0.1)" strokeWidth="0.5" />
            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" className="fill-slate-500" fontSize="9" fontWeight="600">
              {SCORE_META[k].label}
            </text>
          </g>
        );
      })}
      {/* Data polygon */}
      <polygon points={pointsFor(scores)} fill={`${color}20`} stroke={color} strokeWidth="1.5" />
      {/* Data points */}
      {keys.map((k, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const v = (scores[k] || 0) / 100;
        return <circle key={k} cx={cx + r * v * Math.cos(angle)} cy={cy + r * v * Math.sin(angle)} r="3" fill={color} />;
      })}
    </svg>
  );
}

/* ─── Score Bar ──────────────────────────────────────── */
function ScoreBar({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 ${color} shrink-0`} />
      <span className="text-slate-300 text-xs w-12">{label}</span>
      <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.6, delay: 0.2 }}
          className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${color.includes('blue') ? '#3b82f6' : color.includes('emerald') ? '#10b981' : color.includes('amber') ? '#f59e0b' : color.includes('purple') ? '#a855f7' : color.includes('cyan') ? '#06b6d4' : '#f97316'}80, ${color.includes('blue') ? '#3b82f6' : color.includes('emerald') ? '#10b981' : color.includes('amber') ? '#f59e0b' : color.includes('purple') ? '#a855f7' : color.includes('cyan') ? '#06b6d4' : '#f97316'})` }} />
      </div>
      <span className="text-white text-xs font-mono w-8 text-right">{value}</span>
    </div>
  );
}

/* ─── Scenario Selector ─────────────────────────────── */
function ScenarioBar({ scenarios, active, onSelect }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="scenario-bar">
      {Object.entries(scenarios).map(([key, sc]) => {
        const Icon = SCENARIO_ICONS[key] || Scale;
        const isActive = key === active;
        return (
          <button key={key} onClick={() => onSelect(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all shrink-0 ${isActive ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40 shadow-lg shadow-blue-500/10' : 'bg-slate-800/50 text-slate-300 border border-slate-700/40 hover:border-slate-600 hover:text-slate-300'}`}
            data-testid={`scenario-${key}`}>
            <Icon className="w-3.5 h-3.5" />
            {sc.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Path Comparison Card ──────────────────────────── */
function PathCard({ path, isSelected, onSelect, onViewRoadmap, isComparing }) {
  const t = path.template;
  const c = COLOR_MAP[t.color] || COLOR_MAP.blue;
  const Icon = PATH_ICONS[t.icon] || GraduationCap;
  const ds = DESIGNATION_STYLES[path.designation] || DESIGNATION_STYLES.other;
  const DesIcon = ds.icon;
  const [showDetail, setShowDetail] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-2xl border transition-all ${isSelected ? `${ds.border} ring-1 ${c.ring} shadow-lg` : 'border-slate-700/40'} ${isComparing ? 'w-full' : ''}`}
      data-testid={`path-card-${path.id}`}>
      {/* Designation banner */}
      {path.designation !== 'other' && (
        <div className={`absolute top-0 left-0 right-0 h-0.5`} style={{ background: `linear-gradient(90deg, transparent, ${c.hex}, transparent)` }} />
      )}
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 ${c.light} rounded-xl flex items-center justify-center relative`}>
              <Icon className={`w-5 h-5 ${c.text}`} />
              {path.designation === 'primary' && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Crown className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">{t.name}</h3>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${ds.badge}`}>
                <DesIcon className="w-2.5 h-2.5" />{path.designation_label}
              </span>
            </div>
          </div>
          <div className={`px-2.5 py-1 rounded-xl ${c.light} ${c.text} text-sm font-bold tabular-nums`}>
            {path.composite_score}
          </div>
        </div>

        {/* Radar */}
        <div className="w-full aspect-square max-w-[180px] mx-auto mb-3">
          <RadarChart scores={path.scores} color={c.hex} size={180} />
        </div>

        {/* Score bars */}
        <div className="space-y-1.5 mb-3">
          {Object.entries(SCORE_META).map(([k, meta]) => (
            <ScoreBar key={k} label={meta.label} value={path.scores[k]} icon={meta.icon} color={meta.color} />
          ))}
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 bg-slate-900/40 rounded-lg">
            <p className="text-white font-bold text-xs">{t.duration_years}yr</p>
            <p className="text-slate-300 text-[9px]">Duration</p>
          </div>
          <div className="text-center p-2 bg-slate-900/40 rounded-lg">
            <p className="text-white font-bold text-xs">{t.avg_cost_max === 0 ? 'Free' : `$${Math.round(t.avg_cost_max / 1000)}K`}</p>
            <p className="text-slate-300 text-[9px]">Max Cost</p>
          </div>
          <div className="text-center p-2 bg-slate-900/40 rounded-lg">
            <p className="text-emerald-400 font-bold text-xs">${Math.round((path.career_outcome?.entry_salary || 0) / 1000)}K</p>
            <p className="text-slate-300 text-[9px]">Entry Salary</p>
          </div>
        </div>

        {/* 5-year projection */}
        <div className="p-2.5 bg-gradient-to-r from-emerald-500/8 to-cyan-500/5 rounded-xl border border-emerald-500/15 mb-3">
          <div className="flex justify-between items-center">
            <span className="text-slate-300 text-[10px]">5-Year Net Earnings</span>
            <span className="text-emerald-400 font-bold text-sm">${(path.net_five_year || 0).toLocaleString()}</span>
          </div>
        </div>

        {/* Expand for rationale */}
        <button onClick={() => setShowDetail(!showDetail)}
          className={`w-full flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg transition-all border mt-2 ${showDetail ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-slate-800/60 text-blue-400 border-slate-700/50 hover:bg-blue-500/10 hover:border-blue-500/30'}`}
          data-testid={`expand-rationale-${path.id}`}>
          {showDetail ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showDetail ? 'Hide Details' : 'Why This Path?'}
        </button>

        <AnimatePresence>
          {showDetail && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden">
              <div className="pt-3 space-y-3 border-t border-slate-800 mt-2">
                {/* Why it fits */}
                {path.rationale?.why_this_fits?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-emerald-400 mb-1 uppercase tracking-wider">Why This Fits</p>
                    {path.rationale.why_this_fits.map((r, i) => (
                      <p key={i} className="text-slate-300 text-xs flex items-start gap-1.5 mb-1">
                        <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />{r}
                      </p>
                    ))}
                  </div>
                )}
                {/* Tradeoffs */}
                {path.rationale?.tradeoffs?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-amber-400 mb-1 uppercase tracking-wider">Tradeoffs</p>
                    {path.rationale.tradeoffs.map((r, i) => (
                      <p key={i} className="text-slate-300 text-xs flex items-start gap-1.5 mb-1">
                        <ArrowLeftRight className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />{r}
                      </p>
                    ))}
                  </div>
                )}
                {/* Risks */}
                {path.rationale?.main_risks?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-red-400 mb-1 uppercase tracking-wider">Main Risks</p>
                    {path.rationale.main_risks.map((r, i) => (
                      <p key={i} className="text-slate-300 text-xs flex items-start gap-1.5 mb-1">
                        <AlertCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />{r}
                      </p>
                    ))}
                  </div>
                )}
                {/* Best for */}
                {path.rationale?.best_for && (
                  <div className="p-2.5 bg-slate-900/50 rounded-lg">
                    <p className="text-[10px] font-bold text-blue-400 mb-1 uppercase tracking-wider">Best For</p>
                    <p className="text-slate-300 text-xs">{path.rationale.best_for}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <Button size="sm" className={`flex-1 text-xs h-9 font-medium ${isSelected ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-slate-800 hover:bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:border-blue-500/50'}`}
            onClick={() => onSelect(path.id)} data-testid={`select-path-${path.id}`}>
            {isSelected ? <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Selected</> : 'Select This Path'}
          </Button>
          <Button size="sm" className="text-xs h-9 bg-slate-800 hover:bg-purple-600/20 text-purple-400 border border-purple-500/30 hover:border-purple-500/50 font-medium"
            onClick={() => onViewRoadmap(path.id)} data-testid={`roadmap-${path.id}`}>
            Roadmap <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Roadmap Panel ─────────────────────────────────── */
function RoadmapPanel({ roadmap, pathName, onClose }) {
  if (!roadmap) return null;
  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
      className="fixed inset-y-0 right-0 w-full sm:w-96 bg-slate-900 border-l border-slate-700/50 z-50 overflow-y-auto shadow-2xl" data-testid="roadmap-panel">
      <div className="p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white">Roadmap: {pathName}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors" data-testid="close-roadmap">
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </div>

        {/* Stage indicator */}
        <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20 mb-5">
          <p className="text-blue-400 text-sm font-medium">Current Stage: {roadmap.stage_label}</p>
          <p className="text-slate-300 text-xs mt-1">Step {roadmap.current_stage + 1} of {roadmap.total_milestones}</p>
        </div>

        {/* Milestones */}
        <h4 className="text-sm font-bold text-white mb-3">Milestone Timeline</h4>
        <div className="space-y-2 relative mb-6">
          <div className="absolute left-[13px] top-3 bottom-3 w-px bg-slate-700" />
          {roadmap.milestones?.map((m, i) => {
            const cat = MILESTONE_CAT[m.category] || MILESTONE_CAT.planning;
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                className="flex items-start gap-3 relative">
                <div className={`w-7 h-7 rounded-full ${cat.bg} flex items-center justify-center shrink-0 z-10 ring-2 ring-slate-900`}>
                  <div className={`w-2 h-2 rounded-full ${cat.dot}`} />
                </div>
                <div className="flex-1 pb-1">
                  <p className="text-white text-sm font-medium">{m.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-slate-300 text-xs">{m.timeframe}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cat.bg} ${cat.color} capitalize`}>{m.category}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Matched Scholarships */}
        {roadmap.matched_scholarships?.length > 0 && (
          <div className="mb-5">
            <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-blue-400" /> Matched Scholarships</h4>
            <div className="space-y-2">
              {roadmap.matched_scholarships.map((s, i) => (
                <div key={i} className="p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/40">
                  <div className="flex justify-between items-start">
                    <p className="text-white text-xs font-medium">{s.name}</p>
                    <span className="text-emerald-400 text-xs font-mono">${s.amount?.toLocaleString()}</span>
                  </div>
                  {s.deadline && <p className="text-slate-300 text-[10px] mt-1">Deadline: {s.deadline}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matched Programs */}
        {roadmap.matched_programs?.length > 0 && (
          <div className="mb-5">
            <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-purple-400" /> Matched Programs</h4>
            <div className="space-y-2">
              {roadmap.matched_programs.map((p, i) => (
                <div key={i} className="p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/40">
                  <p className="text-white text-xs font-medium">{p.name}</p>
                  <p className="text-slate-300 text-[10px]">{p.org} {p.free ? '(Free)' : `($${p.cost})`}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk + Best For */}
        {roadmap.best_for && (
          <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/40 mb-3">
            <p className="text-[10px] font-bold text-blue-400 mb-1">BEST FOR</p>
            <p className="text-slate-300 text-xs">{roadmap.best_for}</p>
          </div>
        )}
        {roadmap.main_risks?.length > 0 && (
          <div className="p-3 bg-red-500/5 rounded-xl border border-red-500/15">
            <p className="text-[10px] font-bold text-red-400 mb-1">WATCH OUT FOR</p>
            {roadmap.main_risks.map((r, i) => (
              <p key={i} className="text-slate-300 text-xs flex items-start gap-1.5 mb-0.5">
                <AlertCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />{r}
              </p>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Decision Coach (Voice-Enabled) ────────────────── */
function DecisionCoach({ getAuthHeader }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const narration = useNarration();
  const speaking = narration.speaking;
  const messagesEnd = useRef(null);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { return () => narration.stop(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const speakText = useCallback((text) => {
    if (!voiceOn) return;
    narration.say(text);
  }, [voiceOn, narration]);

  const startChat = useCallback(async () => {
    setOpen(true);
    if (messages.length === 0) {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/decision-lab/coach`, { messages: [] }, getAuthHeader());
        setMessages([{ role: 'assistant', content: res.data.response }]);
        speakText(res.data.response);
      } catch {
        const fb = "Hi! I'm your Decision Coach. What matters most to you, keeping costs low, getting to work quickly, or maximizing your long-term potential?";
        setMessages([{ role: 'assistant', content: fb }]);
        speakText(fb);
      } finally { setLoading(false); }
    }
  }, [messages.length, getAuthHeader, speakText]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    narration.stop();
    const userMsg = { role: 'user', content: input.trim() };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs); setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/decision-lab/coach`, { messages: newMsgs }, getAuthHeader());
      setMessages([...newMsgs, { role: 'assistant', content: res.data.response }]);
      speakText(res.data.response);
    } catch {
      setMessages([...newMsgs, { role: 'assistant', content: "I had trouble processing that. Could you rephrase?" }]);
    } finally { setLoading(false); }
  };

  if (!open) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-cyan-600/10 border border-blue-500/20 p-5 cursor-pointer hover:border-blue-500/40 transition-all"
        onClick={startChat} data-testid="coach-toggle">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shrink-0">
            <Compass className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">Decision Coach</h3>
            <p className="text-slate-300 text-sm">Not sure which path? Talk to your AI counselor for personalized guidance</p>
          </div>
          <ChevronRight className="w-5 h-5 text-blue-400 shrink-0 ml-auto" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-slate-900/80 border border-blue-500/20 overflow-hidden backdrop-blur-sm"
      data-testid="decision-coach">
      <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
        <div className="flex items-center gap-3">
          <motion.div animate={speaking ? { scale: [1, 1.1, 1] } : {}} transition={{ repeat: Infinity, duration: 0.8 }}
            className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
            <Compass className="w-5 h-5 text-white" />
          </motion.div>
          <div>
            <p className="text-white text-sm font-bold">Decision Coach</p>
            <p className="text-slate-300 text-[10px]">{speaking ? 'Speaking...' : 'AI-powered guidance'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { if (narration.speaking) narration.stop(); setVoiceOn(v => !v); }}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${voiceOn ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'bg-slate-800 text-slate-300 border border-slate-700/40'}`}
            data-testid="coach-voice-toggle" title={voiceOn ? "Mute voice responses" : "Enable voice responses"}>
            {voiceOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            {voiceOn ? 'Voice On' : 'Voice Off'}
          </button>
          <button onClick={() => { narration.stop(); setOpen(false); }} className="p-1.5 hover:bg-slate-800 rounded-lg" data-testid="close-coach">
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="h-72 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-blue-600/20 text-blue-100 rounded-br-sm' : 'bg-slate-800 text-slate-200 rounded-bl-sm'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 rounded-xl px-3.5 py-2.5 text-sm text-slate-300">
              <span className="animate-pulse">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-800">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about your options..."
            className="flex-1 bg-slate-800 text-white text-sm rounded-xl px-3.5 py-2.5 border border-slate-700/50 focus:outline-none focus:border-blue-500/50 placeholder-slate-500"
            data-testid="coach-input" />
          <Button size="sm" onClick={sendMessage} disabled={loading || !input.trim()} className="h-10 px-4" data-testid="coach-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── MAIN PAGE ─────────────────────────────────────── */
export default function DecisionLab() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('default');
  const [selectedPath, setSelectedPath] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [roadmapName, setRoadmapName] = useState('');
  const [showRoadmap, setShowRoadmap] = useState(false);
  // Child list + active selection now come from ActiveChildContext (sidebar pill)
  const { children, activeChildId, setActiveChildId } = useActiveChild();
  const activeChild = activeChildId;
  const setActiveChild = setActiveChildId;
  const fetchData = useCallback(async (sc) => {
    try {
      setLoading(true);
      const childParam = activeChild ? `&child_id=${activeChild}` : '';
      const res = await axios.get(`${API}/decision-lab/analyze?scenario=${sc}${childParam}`, getAuthHeader());
      setData(res.data);
      if (res.data.paths?.length > 0 && !selectedPath) {
        setSelectedPath(res.data.paths[0].id);
      }
    } catch (err) {
      toast.error('Failed to load Decision Lab');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedPath, activeChild]);

  useEffect(() => { fetchData(scenario); }, [scenario, activeChild]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScenario = (sc) => {
    setScenario(sc);
    setSelectedPath(null);
  };

  const handleViewRoadmap = async (pathId) => {
    try {
      const res = await axios.get(`${API}/decision-lab/roadmap/${pathId}`, getAuthHeader());
      setRoadmap({ ...res.data.roadmap, best_for: res.data.best_for, main_risks: res.data.main_risks, tradeoffs: res.data.tradeoffs });
      setRoadmapName(res.data.pathway_name);
      setShowRoadmap(true);
    } catch {
      toast.error('Failed to load roadmap');
    }
  };

  const PATH_TYPE_MAP = {
    four_year_college: 'college', community_college_transfer: 'college',
    trade_certification: 'trade', military: 'military',
    entrepreneurship: 'entrepreneurship', direct_to_work: 'workforce', career_transition: 'workforce',
  };

  const commitPathAndGo = async () => {
    const sp = paths.find(p => p.id === selectedPath);
    if (!sp) { navigate('/app/pathway'); return; }
    try {
      await axios.post(`${API}/paths/select`, {
        path_type: PATH_TYPE_MAP[sp.id] || 'college',
        institution_name: sp.template?.name || 'My Pathway',
        major_or_track: sp.career_outcome?.title || user?.career_aspirations || sp.template?.name,
        lock_status: 'selected',
        rank: 1,
        notes: `Selected in Decision Lab (score ${sp.composite_score})`,
      }, getAuthHeader());
      toast.success(`${sp.template?.name || 'Path'} saved to your plan`);
    } catch {
      toast.error("Couldn't save your path, continuing anyway.");
    }
    navigate('/app/pathway');
  };

  if (loading && !data) return <AppLayout><div className="flex justify-center py-32"><LoadingSpinner /></div></AppLayout>;

  const paths = data?.paths || [];
  const profile = data?.profile || {};
  const scenarios = data?.available_scenarios || {};
  // Show only top 3 for the selected scenario (not all 6)
  const displayPaths = paths.slice(0, 3);

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="decision-lab-arrival"
        eyebrow="Decision Lab"
        title="Put your options side by side."
        subtitle="Two or three real options, run through the same tests: what each costs, what each earns, how long it takes, and whether it clears the life you designed. No vibes, the same maths applied to every option."
        accent="#a78bfa"
        second="#22d3ee"
        ctaLabel="Compare my options"
        narration={"This is Decision Lab. Most big decisions get made on feeling because nobody ever puts the options in the same units. Here we do. Each option gets costed, priced, timed and judged against the income your life takes, using the same method every time. You will see where they genuinely differ, what each one asks of you, and what would have to be true for the weaker option to win."}
        facts={[
          { icon: A1, head: "Same tests, every option", sub: "One method, so the comparison is fair" },
          { icon: A3, head: "Cost and return", sub: "What you spend, what you earn, when you break even" },
          { icon: A5, head: "Judged against your life", sub: "Does it clear your number?" }
        ]}
        modules={[
          { icon: A2, label: "Add your options", line: "College, trade, certificate, work now, service, business", hue: "#a78bfa" },
          { icon: A3, label: "Total cost", line: "Tuition, aid, interest and the income you give up", hue: "#34d399" },
          { icon: A4, label: "Time to payoff", line: "How long until the choice pays for itself", hue: "#fbbf24" },
          { icon: A5, label: "Fit and outcome", line: "Interest fit, wages and whether it clears your floor", hue: "#22d3ee" },
          { icon: A6, label: "Risks named", line: "What could go wrong with each route", hue: "#f472b6" },
          { icon: A1, label: "A recommendation", line: "A clear call, with the reasoning written down", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      {/* Hero + Child Selector */}
      <div className="mb-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight" data-testid="decision-lab-title">
                Decision <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">Lab</span>
              </h1>
              <p className="text-slate-300 mt-2 text-base sm:text-lg">
                {profile.career_goal
                  ? `Comparing pathways to become a ${profile.career_goal}`
                  : 'Explore and compare every pathway to your future'}
              </p>
            </div>
          </div>
          {/* Parent observer banner, replaces the small "Viewing for:" selector.
              Child switching is now handled by the global sidebar pill. */}
          <ParentViewBanner pageLabel="Decision Lab" />
        </motion.div>
      </div>

      {/* Decision Coach (moved UP, prominent position) */}
      <div className="mb-6">
        <DecisionCoach getAuthHeader={getAuthHeader} />
      </div>

      {/* Scenario Bar */}
      <div className="mb-6">
        <p className="text-xs text-slate-300 mb-2 font-medium uppercase tracking-wider">Choose a Scenario</p>
        <ScenarioBar scenarios={scenarios} active={scenario} onSelect={handleScenario} />
        {data?.scenario_description && (
          <p className="text-slate-300 text-xs mt-2 italic">{data.scenario_description}</p>
        )}
      </div>

      {/* Results Explanation */}
      <motion.div key={scenario} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="mb-4 p-3 rounded-xl bg-gradient-to-r from-blue-500/8 to-purple-500/5 border border-blue-500/15">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-blue-400" />
          <span className="text-white text-sm font-bold">{data?.scenario_label || 'Best Overall'}</span>
          <span className="text-slate-300 text-xs">- showing your top 3 matches</span>
        </div>
        <p className="text-slate-300 text-xs leading-relaxed">{data?.scenario_description}</p>
      </motion.div>

      {/* Path Cards Grid, top 3 only */}
      <div className="grid gap-4 mb-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="paths-grid">
        {displayPaths.map((path) => (
          <PathCard key={path.id} path={path}
            isSelected={selectedPath === path.id}
            onSelect={setSelectedPath}
            onViewRoadmap={handleViewRoadmap}
            isComparing={false} />
        ))}
      </div>

      {/* Selected Path Summary Bar */}
      {selectedPath && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="sticky bottom-4 mx-auto max-w-2xl p-4 bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-blue-500/30 shadow-2xl shadow-blue-500/10 z-20"
          data-testid="selected-summary">
          {(() => {
            const sp = paths.find(p => p.id === selectedPath);
            if (!sp) return null;
            const c = COLOR_MAP[sp.template.color] || COLOR_MAP.blue;
            return (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${c.light} rounded-xl flex items-center justify-center`}>
                    {(() => { const I = PATH_ICONS[sp.template.icon] || GraduationCap; return <I className={`w-5 h-5 ${c.text}`} />; })()}
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{sp.template.name}</p>
                    <p className="text-slate-300 text-xs">{sp.designation_label}, Score: {sp.composite_score}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => handleViewRoadmap(selectedPath)} data-testid="view-selected-roadmap">
                    View Roadmap
                  </Button>
                  {user?.role === 'parent' ? (
                    <Button
                      size="sm"
                      disabled
                      title={`Only ${children.find(c => (c.id || c.user_id) === activeChild)?.full_name?.split(' ')[0] || 'your child'} can set this as their plan`}
                      className="text-xs bg-slate-700 text-slate-300 cursor-not-allowed"
                      data-testid="go-to-journey-disabled"
                    >
                      Only your child can save this
                    </Button>
                  ) : (
                    <Button size="sm" className="text-xs bg-gradient-to-r from-blue-600 to-purple-600" onClick={commitPathAndGo} data-testid="go-to-journey">
                      Start Journey <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* Roadmap Slide-in */}
      <AnimatePresence>
        {showRoadmap && roadmap && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#0a1729]/50 z-40" onClick={() => setShowRoadmap(false)} />
            <RoadmapPanel roadmap={roadmap} pathName={roadmapName} onClose={() => setShowRoadmap(false)} />
          </>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
